import { FunctionComponent, ReactNode } from "react";

import { classNames } from "@/utils/classNames";

import { HelpLauncher } from "@/components/HelpCenter/HelpLauncher";

export interface PageHeaderTab {
	label: ReactNode;
	value: string;
}

interface PageHeaderProps {
	/** Compact page title shown in the header bar. */
	title: ReactNode;
	/** Optional right-aligned content in the title bar (settings icon, export button, …). */
	actions?: ReactNode;
	/** Built-in tab strip rendered directly below the title bar. */
	tabs?: PageHeaderTab[];
	activeTab?: string;
	onTabChange?: (value: string) => void;
	/** Custom navigation strip (e.g. a tabs component with badges) rendered below the title bar. */
	children?: ReactNode;
}

/**
 * Shared page header for the main app surfaces. Mirrors the List overview header
 * (`ListOverview` + `ListOwnershipTabs`): a compact title bar with the tab strip directly
 * below it — no gap in between.
 *
 * It is full-bleed: the `MainLayout` outlet wraps pages in `px-4 pt-3`, so the header negates
 * that padding (`-ml-4 -mt-3` + a `calc(100% + 2rem)` width) and spans edge-to-edge, exactly
 * like the List page (which renders in the padding-free `ListLayout`).
 */
export const PageHeader: FunctionComponent<PageHeaderProps> = ({
	title,
	actions,
	tabs,
	activeTab,
	onTabChange,
	children,
}) => (
	<div className="-ml-4 -mt-3 mb-4 w-[calc(100%_+_2rem)] bg-white">
		<div className="flex h-12 items-center gap-2.5 border-b border-border-light px-4">
			<h1 className="whitespace-nowrap text-sm font-semibold text-text">{title}</h1>
			{/* The help launcher renders on every header (it self-gates on the HELP_CENTER flag). */}
			<div className="ml-auto flex items-center gap-2">
				{actions}
				<HelpLauncher />
			</div>
		</div>

		{tabs && tabs.length > 0 && (
			<div className="flex overflow-x-auto border-b border-border-light">
				{tabs.map((tab) => (
					<button
						key={tab.value}
						className={classNames(
							"cursor-pointer whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors",
							activeTab === tab.value
								? "border-brand-light text-brand-light"
								: "border-transparent text-text-meta hover:border-gray-300 hover:text-text"
						)}
						type="button"
						onClick={() => onTabChange?.(tab.value)}
					>
						{tab.label}
					</button>
				))}
			</div>
		)}

		{children}
	</div>
);
